import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { WorkloadTrends } from '../components/rd/WorkloadTrends';
import { UsersRound, ListChecks, CalendarClock, AlertTriangle, Pause, Hourglass, ChevronRight } from 'lucide-react';

const SEGMENTS = [
  ['blocked', 'bg-rose-500'],
  ['delayed', 'bg-amber-400'],
  ['on_track', 'bg-slate-400'],
  ['advanced', 'bg-blue-500'],
];
const SORTS = {
  urgency: (a, b) => (b.overdue - a.overdue) || (b.blocked - a.blocked) || (b.open - a.open),
  open: (a, b) => b.open - a.open,
  name: (a, b) => a.name.localeCompare(b.name),
};

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${tone}`} data-testid={testId}>
    <Icon className="w-5 h-5 shrink-0" />
    <div>
      <p className="text-2xl font-semibold leading-none font-mono">{value}</p>
      <p className="text-[11px] uppercase tracking-wide mt-1 opacity-80">{label}</p>
    </div>
  </div>
);

const LoadBar = ({ row, max }) => (
  <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden flex" style={{ maxWidth: `${Math.max(8, (row.open / max) * 100)}%` }}>
    {SEGMENTS.map(([k, cls]) => row[k] > 0 && (
      <div key={k} className={`${cls} h-full`} style={{ width: `${(row[k] / row.open) * 100}%` }} title={`${row[k]} ${k.replace('_', ' ')}`} />
    ))}
  </div>
);

const Num = ({ v, alert, testId }) => (
  <span className={`font-mono text-sm ${v > 0 && alert ? alert : v > 0 ? 'text-slate-800' : 'text-slate-300'}`} data-testid={testId}>{v}</span>
);

const PersonRow = ({ row, max, onOpen }) => {
  const tid = row.key.replace(/[^a-z0-9-]/gi, '-');
  return (
    <button
      type="button"
      disabled={!row.is_user}
      onClick={() => onOpen(row)}
      className={`w-full grid grid-cols-[1fr_repeat(5,3.5rem)_1.25rem] md:grid-cols-[14rem_repeat(5,4rem)_1fr_1.25rem] gap-3 items-center px-4 py-3 text-left transition-colors ${row.is_user ? 'hover:bg-fuchsia-50 cursor-pointer' : 'cursor-default'}`}
      data-testid={`workload-row-${tid}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate" data-testid={`workload-name-${tid}`}>{row.name}</p>
        <p className="text-[11px] text-slate-500 truncate">
          {row.is_user ? (row.role || 'User') : row.key === 'unassigned' ? 'No one assigned' : 'Not a system user'}
          {row.project_count > 0 && ` · ${row.project_count} project${row.project_count > 1 ? 's' : ''}`}
        </p>
      </div>
      <Num v={row.open} testId={`workload-open-${tid}`} />
      <Num v={row.overdue} alert="text-rose-600 font-bold" testId={`workload-overdue-${tid}`} />
      <Num v={row.due_soon} alert="text-orange-600" testId={`workload-due-soon-${tid}`} />
      <Num v={row.blocked} alert="text-rose-700" testId={`workload-blocked-${tid}`} />
      <Num v={row.delayed} alert="text-amber-700" testId={`workload-delayed-${tid}`} />
      <div className="hidden md:block">{row.open > 0 ? <LoadBar row={row} max={max} /> : <span className="text-[11px] text-slate-400">Free</span>}</div>
      {row.is_user ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <span />}
    </button>
  );
};

export default function RDTeamWorkloadPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState('urgency');

  useEffect(() => {
    api().get('/rd/team-workload')
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to load workload'));
  }, [api]);

  const rows = useMemo(() => [...(data?.rows || [])].sort(SORTS[sort]), [data, sort]);
  const max = Math.max(1, ...rows.map(r => r.open));
  const t = data?.totals || {};

  return (
    <div className="p-6 space-y-6" data-testid="rd-team-workload-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-slate-800">
            <UsersRound className="w-6 h-6 text-fuchsia-600" /> Team Workload
          </h1>
          <p className="text-sm text-slate-500 mt-1">Open R&D tasks per person across every project. Click someone to see their task list.</p>
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-48" data-testid="workload-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="urgency">Most urgent first</SelectItem>
            <SelectItem value="open">Most open tasks</SelectItem>
            <SelectItem value="name">Name A–Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={ListChecks} label="Open" value={t.open || 0} tone="bg-white border-slate-200 text-slate-700" testId="workload-total-open" />
        <Stat icon={CalendarClock} label="Overdue" value={t.overdue || 0} tone="bg-rose-50 border-rose-200 text-rose-700" testId="workload-total-overdue" />
        <Stat icon={Hourglass} label="Due ≤ 3 days" value={t.due_soon || 0} tone="bg-orange-50 border-orange-200 text-orange-700" testId="workload-total-due-soon" />
        <Stat icon={AlertTriangle} label="Blocked" value={t.blocked || 0} tone="bg-rose-50/60 border-rose-100 text-rose-800" testId="workload-total-blocked" />
        <Stat icon={Pause} label="Delayed" value={t.delayed || 0} tone="bg-amber-50 border-amber-200 text-amber-800" testId="workload-total-delayed" />
      </div>

      <Card className="overflow-hidden" data-testid="workload-table">
        <div className="grid grid-cols-[1fr_repeat(5,3.5rem)_1.25rem] md:grid-cols-[14rem_repeat(5,4rem)_1fr_1.25rem] gap-3 px-4 py-2 bg-slate-50 border-b text-[11px] uppercase tracking-wide text-slate-500">
          <span>Person</span><span>Open</span><span>Overdue</span><span>Due 3d</span><span>Blocked</span><span>Delayed</span>
          <span className="hidden md:flex items-center gap-3">
            Load
            {SEGMENTS.map(([k, cls]) => <span key={k} className="inline-flex items-center gap-1 normal-case tracking-normal"><i className={`w-2 h-2 rounded-full ${cls}`} />{k.replace('_', ' ')}</span>)}
          </span>
          <span />
        </div>
        {!data ? (
          <p className="p-6 text-sm text-slate-500" data-testid="workload-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-500" data-testid="workload-empty">No open R&D tasks and no engineers yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(r => <PersonRow key={r.key} row={r} max={max} onOpen={(row) => navigate(`/rd/my-tasks?user=${row.user_id}`)} />)}
          </div>
        )}
      </Card>

      <WorkloadTrends />
    </div>
  );
}
